import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const services = [
  {
    id: 'custom-automation',
    number: '01',
    title: 'Custom Automation Development',
    intro:
      'We analyze your existing workflows and build automation systems that eliminate repetitive manual work, reduce errors, and free your team to focus on revenue-generating activities.',
    detail:
      'No off-the-shelf tools — everything is built for your specific business, your specific team, and your specific growth stage. From simple task automation to complex multi-step workflows, we build systems that run without you.',
    includes: [
      'Workflow discovery and process mapping',
      'Custom automation build and integration',
      'Testing, deployment, and documentation',
      'Ongoing support and iteration',
    ],
  },
  {
    id: 'outreach-systems',
    number: '02',
    title: 'Outreach Systems',
    intro:
      'We design and build end-to-end automated outreach pipelines — from prospect identification to personalized email sequences to reply handling. Built to fill your calendar without adding headcount.',
    detail:
      'Every outreach system we build is personalized at scale. Not mail merge — genuine, signal-driven personalization that gets responses. We handle the entire stack: data sourcing, copy, sequencing, reply routing, and reporting.',
    includes: [
      'Ideal customer profile definition and targeting',
      'Personalized multi-channel email sequences',
      'Automated reply handling and routing',
      'Calendar integration and meeting booking',
      'Performance reporting and optimization',
    ],
  },
  {
    id: 'lead-generation',
    number: '03',
    title: 'Lead Generation',
    intro:
      'We build signal-based lead generation systems that identify high-intent prospects at the right moment — job changes, funding events, trigger signals — and route them into your pipeline automatically.',
    detail:
      'Instead of static lists, we build systems that respond to real-world signals. When a prospect\'s situation changes in a way that makes them a better fit for your business, your pipeline knows — automatically.',
    includes: [
      'Signal source identification and configuration',
      'Automated prospect enrichment and scoring',
      'CRM integration and pipeline routing',
      'Real-time alerts and notifications',
      'Reporting and pipeline analytics',
    ],
  },
];

export default function Services() {
  return (
    <div className="min-h-screen bg-cream font-sans">
      <Nav />

      {/* ── Page Header ──────────────────────────────────── */}
      <section className="pt-28 lg:pt-36 pb-16 border-b border-warm-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              What We Build
            </p>
            <h1 className="font-display text-5xl lg:text-[3.5rem] font-bold text-charcoal leading-tight mb-7">
              Services
            </h1>
            <p className="text-warm-gray text-xl leading-relaxed">
              Jambition builds custom AI automation systems for professional
              services firms. No off-the-shelf tools. No generic playbooks.
              Every engagement starts with a free AI audit to identify exactly
              where automation will have the greatest impact.
            </p>
          </div>
        </div>
      </section>

      {/* ── Service Sections ─────────────────────────────── */}
      {services.map((service, i) => (
        <section
          key={service.number}
          id={service.id}
          className={`py-20 border-b border-warm-border ${
            i % 2 === 0 ? 'bg-white' : 'bg-cream'
          }`}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Left: label + title */}
              <div className="lg:col-span-2">
                <p className="text-amber-600 font-semibold text-xs tracking-[0.2em] uppercase mb-4">
                  Service {service.number}
                </p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold text-charcoal leading-tight">
                  {service.title}
                </h2>
              </div>

              {/* Right: copy */}
              <div className="lg:col-span-3">
                <p className="text-warm-gray text-lg leading-relaxed mb-5">
                  {service.intro}
                </p>
                <p className="text-warm-gray text-lg leading-relaxed">
                  {service.detail}
                </p>

                <div className="border-t border-warm-border pt-8 mt-10">
                  <p className="text-sm font-semibold text-charcoal uppercase tracking-widest mb-5">
                    What this typically includes
                  </p>
                  <ul className="space-y-3">
                    {service.includes.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 text-warm-gray"
                      >
                        <span className="mt-2 w-1.5 h-1.5 bg-amber-600 rounded-full flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10">
                  <p className="text-charcoal font-semibold mb-4">
                    Let's talk about what this looks like for your business.
                  </p>
                  <Link
                    to="/audit"
                    className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 text-sm transition-colors"
                  >
                    Get Started <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="py-24 bg-amber-600">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-bold text-white mb-5">
            Every engagement starts with a free audit.
          </h2>
          <p className="text-amber-100 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            No pitch, no pressure. Just a clear picture of where automation can
            make the biggest difference for your business.
          </p>
          <Link
            to="/audit"
            className="inline-flex items-center gap-2 bg-white text-amber-700 hover:bg-amber-50 font-bold px-9 py-4 text-base transition-colors"
          >
            Get Your Free AI Audit <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
